/**
 * Ritual homepage helpers.
 *
 * - Reveal-on-scroll for elements marked `data-ritual-reveal` (IntersectionObserver, ~0.5 KB).
 * - Inline vote tiles for the "settle this for us" section: tapping a tile records the choice
 *   into the customer form's `contact[tags]` field and reveals the editor-set results.
 *
 * No dependencies, no layout thrash, respects prefers-reduced-motion via CSS.
 */
document.documentElement.classList.add('ritual-js');

// Each feature runs on its own: an error in one must never stop the others (the sign-up flow above all).
const safe = (fn) => {
  try {
    fn();
  } catch (err) {
    console.error('ritual.js', err);
  }
};

safe(function reveal() {
  const targets = document.querySelectorAll('[data-ritual-reveal]');
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );

  targets.forEach((el) => observer.observe(el));
});

safe(function vote() {
  document.querySelectorAll('[data-ritual-vote]').forEach((section) => {
    const tiles = section.querySelectorAll('[data-ritual-vote-tile]');
    const tagsInput = section.querySelector('[data-ritual-vote-tags], [data-ritual-signup-tags]');
    const baseTags = tagsInput ? tagsInput.value : '';

    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        tiles.forEach((t) => {
          const selected = t === tile;
          t.classList.toggle('is-selected', selected);
          t.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        if (tagsInput) {
          const tag = tile.dataset.ritualVoteTile;
          tagsInput.value = baseTags ? `${baseTags},${tag}` : tag;
        }

        section.classList.add('has-vote');

        const email = section.querySelector('input[type="email"]');
        if (email && window.matchMedia('(min-width: 750px)').matches) email.focus({ preventScroll: true });
      });
    });
  });
});

safe(function sheep() {
  // A welcome pop-up sign-up posts the same customer form; that one is not a vote.
  if (/^#Ritual(Popup|Newsletter|Vote)Form/.test(window.location.hash)) return;
  document.querySelectorAll('[data-ritual-sheep]').forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    const close = () => dialog.close();
    dialog.querySelectorAll('[data-ritual-sheep-close]').forEach((b) => b.addEventListener('click', close));
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) close();
    });
    try {
      dialog.showModal();
    } catch (_) {
      dialog.setAttribute('open', '');
    }
  });
});

safe(function countUp() {
  const nodes = document.querySelectorAll('[data-ritual-count]');
  if (!nodes.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fmt = (n) => Math.round(n).toLocaleString();

  const run = (el) => {
    const target = parseFloat(el.dataset.ritualCount);
    if (!Number.isFinite(target) || reduce) {
      el.textContent = fmt(target);
      return;
    }
    const duration = 1400;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = fmt(target * eased);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  if (!('IntersectionObserver' in window)) {
    nodes.forEach(run);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        run(e.target);
        io.unobserve(e.target);
      });
    },
    { threshold: 0.4 }
  );
  nodes.forEach((el) => {
    el.textContent = '0';
    io.observe(el);
  });
});

safe(function journeys() {
  const cols = Array.from(document.querySelectorAll('[data-ritual-journey]'));
  if (!cols.length || !('IntersectionObserver' in window)) return;
  const sideBySide = window.matchMedia('(min-width: 990px)');

  // Desktop: the columns sit side by side, so only the "engaged" column (hovered, tapped or
  // focused) follows the scroll; the other freezes. Stacked (mobile/tablet): both follow.
  let engaged = null;
  const setEngaged = (col) => {
    engaged = col;
    cols.forEach((c) => c.classList.toggle('is-frozen', sideBySide.matches && engaged !== null && c !== engaged));
  };
  const listen = (col) => {
    ['pointerenter', 'pointerdown', 'focusin'].forEach((evt) => col.addEventListener(evt, () => setEngaged(col)));
  };
  sideBySide.addEventListener('change', () => setEngaged(sideBySide.matches ? engaged : null));

  cols.forEach((col) => {
    const steps = Array.from(col.querySelectorAll('[data-ritual-step]'));
    const frames = col.querySelectorAll('[data-ritual-frame]');
    const pips = col.querySelectorAll('[data-ritual-pip]');
    if (!steps.length) return;
    listen(col);

    const activate = (index) => {
      steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
      frames.forEach((f, i) => f.classList.toggle('is-active', i === index));
      pips.forEach((p, i) => p.classList.toggle('is-active', i === index));
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (sideBySide.matches && engaged && engaged !== col) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate(Number(entry.target.dataset.ritualStep));
        });
      },
      // Active band sits just under the pinned stage on phones, mid-screen on desktop.
      { rootMargin: window.innerWidth < 600 ? '-42% 0px -38% 0px' : '-40% 0px -45% 0px', threshold: 0 }
    );
    steps.forEach((s) => io.observe(s));
    steps.forEach((s) => s.addEventListener('click', () => activate(Number(s.dataset.ritualStep))));
  });

  // Start with the left column engaged so a plain scroll tells one story at a time.
  if (sideBySide.matches) setEngaged(cols[0]);
});

// External links in the hero trust line (certification pages) open in a new tab.
document.querySelectorAll('.ritual-hero__trust a[href^="http"]').forEach((a) => {
  if (a.host !== window.location.host) {
    a.target = '_blank';
    a.rel = 'noopener';
  }
});

safe(function drawers() {
  const wire = (dialog) => {
    if (dialog.dataset.ritualWired) return;
    dialog.dataset.ritualWired = 'true';
    dialog.querySelectorAll('[data-ritual-drawer-close]').forEach((c) => c.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  };
  const open = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return false;
    wire(dialog);
    if (!dialog.open) dialog.showModal();
    return true;
  };
  const firstDrawer = () => document.querySelector('dialog[data-ritual-drawer]');

  // Delegated, so buttons still work after Fabric re-renders the hero on a variant change.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-ritual-drawer-open]');
    if (btn) {
      e.preventDefault();
      open(document.getElementById(btn.dataset.ritualDrawerOpen));
      return;
    }
    // Any "#size-guide" link (FAQ, footer, product copy) opens the size guide drawer;
    // pages without one go home and open it there.
    const link = e.target.closest?.('a[href="#size-guide"], a[href="/#size-guide"]');
    if (link) {
      if (open(firstDrawer())) {
        e.preventDefault();
      } else {
        link.setAttribute('href', '/#size-guide');
      }
    }
  });

  if (window.location.hash === '#size-guide') {
    const drawer = firstDrawer();
    if (drawer) setTimeout(() => open(drawer), 300);
  }
});

safe(function tips() {
  const pills = document.querySelectorAll('[data-ritual-tip]');
  if (!pills.length) return;
  const closeAll = (except) => pills.forEach((p) => p !== except && p.setAttribute('aria-expanded', 'false'));
  pills.forEach((pill) => {
    pill.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = pill.getAttribute('aria-expanded') === 'true';
      closeAll(pill);
      pill.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  });
  document.addEventListener('click', () => closeAll());
  document.addEventListener('keydown', (e) => e.key === 'Escape' && closeAll());
});

safe(function horizontalTimeline() {
  const sections = document.querySelectorAll('[data-ritual-hscroll]');
  if (!sections.length) return;
  const pinned = window.matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');

  sections.forEach((section) => {
    const track = section.querySelector('[data-ritual-track]');
    const viewport = section.querySelector('[data-ritual-viewport]');
    const bar = section.querySelector('[data-ritual-progress]');
    if (!track || !viewport) return;
    let ticking = false;
    // Phones: the row scrolls natively. Keep the "keep going" cues until the last card is reached,
    // and let the arrow advance one card per tap.
    const next = section.querySelector('[data-ritual-next]');
    // At the end when the scroll range is used up, or when the last card is fully in view (on narrow phones
    // mandatory snap parks on the last card's start and leaves a little range unused).
    const lastCard = track.lastElementChild;
    const atEnd = () =>
      viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 8 ||
      Boolean(lastCard && lastCard.getBoundingClientRect().right <= viewport.getBoundingClientRect().right + 1);
    // The end cue only means something in the swipe layout; in the pinned layout the row is translated instead.
    const syncEnd = () => section.classList.toggle('is-end', atEnd());
    viewport.addEventListener('scroll', () => {
      if (!pinned.matches) syncEnd();
    }, { passive: true });
    if (next) {
      next.addEventListener('click', () => {
        // One card per tap: card width plus the track's real gap (history cards carry no data-step).
        const card = track.querySelector('[data-step]') || track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 28;
        const step = card ? card.getBoundingClientRect().width + gap : viewport.clientWidth * 0.8;
        viewport.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    let wasPinned = null;
    const update = () => {
      ticking = false;
      if (!pinned.matches) {
        track.style.transform = '';
        if (wasPinned !== false) syncEnd(); // back in the swipe layout: recompute the end cue once
        wasPinned = false;
        return;
      }
      // Entering the pinned layout (load, rotation or resize past 750px): drop any swipe position left over from
      // the phone layout, otherwise it adds to the translate and pushes the cards off screen. Only on entry, so
      // the browser can still scroll a keyboard-focused link into view afterwards.
      if (wasPinned !== true && viewport.scrollLeft) viewport.scrollLeft = 0;
      wasPinned = true;
      const rect = section.getBoundingClientRect();
      const runway = rect.height - window.innerHeight;
      const progress = runway > 0 ? Math.min(1, Math.max(0, -rect.top / runway)) : 0;
      const max = Math.max(0, track.scrollWidth - viewport.clientWidth);
      track.style.transform = `translate3d(${-max * progress}px, 0, 0)`;
      if (bar) bar.style.transform = `scaleX(${progress})`;
    };
    const request = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    // Fabric scrolls the page inside .page-wrapper on desktop, so listen in capture phase.
    document.addEventListener('scroll', request, { capture: true, passive: true });
    window.addEventListener('resize', request, { passive: true });
    pinned.addEventListener('change', request);
    update();
  });
});

/**
 * "Chat with us" inside the size guide: close the drawer, then open Shopify Inbox.
 * Inbox has no official JS API, so we try the known hooks in order and fall back to the
 * contact page if the widget is not on the page (or is blocked).
 */
safe(function openChat() {
  const tryOpen = () => {
    const api = window.ShopifyChat || window.Shopify?.Chat || window.shopifyChat;
    if (api && typeof api.open === 'function') {
      api.open();
      return true;
    }
    const host = document.querySelector('#shopify-chat, shopify-chat, #ShopifyChat, [id^="shopify-chat"]');
    if (host) {
      const target = host.shadowRoot?.querySelector('button') || host.querySelector?.('button') || host;
      if (target && typeof target.click === 'function') {
        target.click();
        return true;
      }
    }
    return false;
  };

  const open = (fallback) => {
    if (tryOpen()) return;
    // Widget may still be loading; give it a moment before falling back.
    setTimeout(() => {
      if (!tryOpen() && fallback) window.location.href = fallback;
    }, 400);
  };

  document.querySelectorAll('[data-ritual-open-chat]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dialog = btn.closest('dialog');
      if (dialog && typeof dialog.close === 'function') dialog.close();
      open(btn.dataset.fallback);
    });
  });

  // Any link to "#chat" (rich text in the FAQ, product copy, pages) opens the Inbox widget too.
  document.addEventListener('click', (e) => {
    const link = e.target.closest?.('a[href="#chat"]');
    if (!link) return;
    e.preventDefault();
    open(link.dataset.fallback || '/pages/contact');
  });
});

/* Multi-step sign-up (snippets/ritual-signup.liquid), used by the welcome pop-up and the newsletter
   section. Step 1 posts the native customer form in the background (name, email, consent, tag);
   step 2 (optional phone, only when Klaviyo is configured) posts to Klaviyo with SMS consent;
   step 3 is the thank-you. */
safe(function signupFlows() {
  const roots = document.querySelectorAll('[data-ritual-signup]');
  if (!roots.length) return;
  const hash = window.location.hash;

  roots.forEach((root) => {
    const form = root.querySelector('form');
    if (!form) return;
    const posted = root.querySelector('[data-ritual-signup-posted]');
    const step = (n) => {
      root.dataset.step = String(n);
      root.querySelectorAll('[data-ritual-signup-step]').forEach((el) => {
        el.hidden = el.dataset.ritualSignupStep !== String(n);
      });
    };
    const showError = (el, msg) => {
      if (!el) return;
      el.textContent = msg;
      el.hidden = !msg;
    };

    // No-JS fallback markup: only reveal it when this very form was the one posted.
    if (posted) {
      if (hash === '#' + root.dataset.formId) {
        posted.style.display = '';
        root.querySelectorAll('[data-ritual-signup-step]').forEach((el) => (el.hidden = true));
        root.dispatchEvent(new CustomEvent('ritual:signup-done', { bubbles: true }));
        return;
      }
      posted.remove();
    }

    // Sub-step A -> B: a name, then the email bubble (with consent) takes its place.
    const nameInput = root.querySelector('[data-ritual-signup-name]');
    const sub = (which) => root.querySelectorAll('[data-ritual-signup-sub]').forEach((el) => {
      el.hidden = el.dataset.ritualSignupSub !== which;
    });
    const goEmail = () => {
      const name = (nameInput?.value || '').trim();
      if (!name) {
        nameInput?.focus();
        nameInput?.setCustomValidity('Please add your name');
        nameInput?.reportValidity();
        return;
      }
      nameInput?.setCustomValidity('');
      // Separate last-name bubble when present; otherwise split "First Last" on the first space.
      const lastInput = root.querySelector('[data-ritual-signup-lastname]');
      let first = name;
      let last = (lastInput?.value || '').trim();
      if (!lastInput) {
        const parts = name.split(/\s+/);
        first = parts.shift();
        last = parts.join(' ');
      }
      const firstEl = root.querySelector('[data-ritual-signup-first]');
      const lastEl = root.querySelector('[data-ritual-signup-last]');
      if (firstEl) firstEl.value = first;
      if (lastEl) lastEl.value = last;
      sub('email');
      root.querySelector('input[type="email"]')?.focus();
    };
    root.querySelector('[data-ritual-signup-name-next]')?.addEventListener('click', goEmail);
    // The last-name bubble slides open beside the first name as soon as something is typed.
    const namesRow = root.querySelector('.ritual-signup__row--names');
    const lastInput = root.querySelector('[data-ritual-signup-lastname]');
    // On a wide row (the newsletter) both bubbles show from the start.
    const fields = root.querySelector('.ritual-signup__fields');
    const openLast = () => {
      const wide = (fields?.clientWidth || 0) >= 560;
      const open = wide || Boolean(nameInput?.value.trim());
      namesRow?.classList.toggle('is-open', open);
      if (lastInput) lastInput.tabIndex = open ? 0 : -1;
    };
    nameInput?.addEventListener('input', () => {
      nameInput.setCustomValidity('');
      openLast();
    });
    openLast();
    root.querySelectorAll('[data-ritual-signup-name], [data-ritual-signup-lastname]').forEach((el) =>
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          goEmail();
        }
      })
    );

    // Submit in place: the visitor never leaves the page. Klaviyo (when configured) takes the
    // email consent, and Shopify's customer form is posted alongside as a best effort, because
    // Shopify sometimes answers it with a bot-check page that only a full navigation could pass.
    const phoneStep = root.querySelector('[data-ritual-signup-phone]');
    const klaviyo = phoneStep && phoneStep.dataset.company && phoneStep.dataset.list ? phoneStep.dataset : null;
    const val = (name) => form.querySelector(`input[name="${name}"]`)?.value || undefined;
    const postShopify = () =>
      fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(form)),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'text/html' },
        credentials: 'same-origin',
        redirect: 'follow',
        keepalive: true,
      }).then((res) => res.ok && !/\/challenge/.test(res.url || ''));
    const postKlaviyoEmail = () =>
      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(klaviyo.company)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Ritual sign-up (' + (root.dataset.formId || 'form') + ')',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: val('contact[email]'),
                    first_name: val('contact[first_name]'),
                    last_name: val('contact[last_name]'),
                    properties: { ritual_tags: val('contact[tags]') },
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: klaviyo.list } } },
          },
        }),
      }).then((res) => res.ok);

    form.addEventListener('submit', async (e) => {
      e.preventDefault(); // never a full-page post: no captcha page, no reload, no scroll
      if (!form.reportValidity()) return; // the browser points at what is missing
      const btn = form.querySelector('[data-ritual-signup-next]');
      const err = form.querySelector('[data-ritual-signup-error]');
      showError(err, '');
      if (btn) btn.disabled = true;
      const settle = (p) => p.then((ok) => Boolean(ok)).catch(() => false);
      const results = await Promise.all([settle(postShopify()), klaviyo ? settle(postKlaviyoEmail()) : Promise.resolve(false)]);
      if (results.some(Boolean)) {
        root.dispatchEvent(new CustomEvent('ritual:signup-done', { bubbles: true }));
        step(phoneStep ? 2 : 3);
        return;
      }
      if (btn) btn.disabled = false;
      showError(err, 'Something went wrong on our side. Please try again in a moment.');
    });

    const phone = phoneStep;
    if (!phone) return;
    root.querySelector('[data-ritual-signup-skip]')?.addEventListener('click', () => step(3));
    root.querySelector('[data-ritual-signup-phone-submit]')?.addEventListener('click', async (ev) => {
      const btn = ev.currentTarget;
      const err = phone.querySelector('[data-ritual-signup-phone-error]');
      const dial = phone.querySelector('[data-ritual-signup-dial]')?.value || '+1';
      const raw = (phone.querySelector('[data-ritual-signup-tel]')?.value || '').replace(/\D/g, '');
      if (raw.length < 6) {
        showError(err, 'Please enter a phone number, or skip.');
        return;
      }
      showError(err, '');
      btn.disabled = true;
      const number = dial + raw.replace(/^0+/, '');
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Ritual sign-up (' + (root.dataset.formId || 'form') + ')',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: val('contact[email]'),
                  first_name: val('contact[first_name]'),
                  last_name: val('contact[last_name]'),
                  phone_number: number,
                  subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: phone.dataset.list } } },
        },
      };
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(phone.dataset.company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify(payload),
        });
        if (!res.ok && res.status !== 202) {
          // SMS not switched on in Klaviyo yet: keep the number on the profile so nothing is lost.
          const saved = await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(phone.dataset.company)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'profile',
                attributes: {
                  email: val('contact[email]'),
                  first_name: val('contact[first_name]'),
                  last_name: val('contact[last_name]'),
                  properties: { ritual_phone: number, ritual_sms_optin: new Date().toISOString(), ritual_tags: val('contact[tags]') },
                },
              },
            }),
          });
          if (!saved.ok && saved.status !== 202) throw new Error('bad status');
        }
        const reward = root.querySelector('[data-ritual-signup-reward]');
        const plain = root.querySelector('[data-ritual-signup-plain]');
        if (reward) reward.hidden = false;
        if (reward && plain && reward.querySelector('a')) plain.hidden = true;
        step(3);
      } catch (_) {
        showError(err, 'That number did not go through. Check it, or skip for now.');
        btn.disabled = false;
      }
    });
  });
});

/* Welcome pop-up: once per visitor, after a delay or on exit intent, never over another dialog.
   Hidden for N days after a close, for good after a sign-up. The form inside is a ritual-signup. */
safe(function welcomePopup() {
  const dialog = document.querySelector('[data-ritual-popup]');
  if (!(dialog instanceof HTMLDialogElement)) return;
  const KEY = 'ritual-popup';
  const read = () => {
    try {
      return localStorage.getItem(KEY) || '';
    } catch (_) {
      return '';
    }
  };
  const write = (v) => {
    try {
      localStorage.setItem(KEY, v);
    } catch (_) {}
  };
  const days = Number(dialog.dataset.days || 7);
  const state = read();
  const posted = window.location.hash === '#RitualPopupForm';

  const close = () => {
    if (dialog.open) dialog.close();
  };
  dialog.querySelectorAll('[data-ritual-popup-close]').forEach((b) => b.addEventListener('click', close));
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
  dialog.addEventListener('close', () => {
    if (read() !== 'done') write('dismissed:' + Date.now());
  });
  // Any sign-up on the site (pop-up or newsletter section) means we never need to ask again.
  document.addEventListener('ritual:signup-done', () => write('done'));

  const open = () => {
    if (dialog.open) return;
    if (document.querySelector('dialog[open]:not([data-ritual-popup])')) return; // something else is up
    try {
      dialog.showModal();
    } catch (_) {
      dialog.setAttribute('open', '');
    }
    const input = dialog.querySelector('[data-ritual-signup-name], input[type="email"]');
    if (input && window.matchMedia('(min-width: 750px)').matches) input.focus();
  };

  if (posted) {
    write('done');
    open();
    return;
  }
  if (state === 'done') return;
  if (state.startsWith('dismissed:')) {
    const at = Number(state.split(':')[1] || 0);
    if (Date.now() - at < days * 86400000) return;
  }

  let armed = true;
  const fire = () => {
    if (!armed) return;
    armed = false;
    open();
  };
  setTimeout(fire, Math.max(1, Number(dialog.dataset.delay || 7)) * 1000);
  if (dialog.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mouseout', (e) => {
      if (!e.relatedTarget && e.clientY <= 0) fire();
    });
  }
});

/* Quick-add ("Choose size") pop-up: keep a collapsible size guide right under the size buttons.
   Fabric re-renders the modal body on open and on variant change, so re-insert whenever it is missing. */
safe(function quickAddSizeGuide() {
  const tpl = document.querySelector('[data-ritual-quick-size-template]');
  const modal = document.getElementById('quick-add-modal-content');
  if (!(tpl instanceof HTMLTemplateElement) || !modal) return;
  const place = () => {
    if (modal.querySelector('.ritual-quick-size')) return;
    const picker = modal.querySelector('variant-picker');
    if (!picker) return;
    const node = tpl.content.firstElementChild?.cloneNode(true);
    if (node) picker.insertAdjacentElement('afterend', node);
  };
  new MutationObserver(place).observe(modal, { childList: true, subtree: true });
  document.addEventListener('toggle', place, true);
  document.addEventListener('click', () => setTimeout(place, 50), true);
  place();
});
